/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useRef, useEffect } from "react";
import useForm from "react-hook-form";
import {
  authCheckCallApi,
  getPlanInfo
} from "../../Authenticator/utils";
import { countryOptions } from "../../Authenticator/countries";
import { AuthProps } from "aws-amplify-react/props";
import { useLazyQuery } from "@apollo/react-hooks";
import { useMutation } from "@apollo/react-hooks";
import {
  OrganizationQuery,
  UpdateOrganizationMutation,
  OrganizationInput,
  OrganizationData,
} from "../../../graphql/organization";
import {
  Segment,
  Transition,
  Container,
  Form,
  Button,
  Dimmer,
  Loader,
  Grid,
  Dropdown,
  DropdownProps,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import { Ico } from "../../Icon/Icon";
import ConfirmationDialog from "../../ConfirmationDialog";
import { useTranslation } from "react-i18next";

const Company: React.FC<AuthProps> = ({ authData }) => {
  const currentOrganizationUpdated = useRef<number>();
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const { register, handleSubmit, errors, setValue } = useForm<FormData>();
  const isAdmin = authData.signInUserSession.idToken.payload.role !== "USER";
  const { t } = useTranslation("common");

  useEffect(() => {
    register({ name: "addressCountry" }, { required: true });
  }, [register]);

  const [
    getCompany,
    { loading: queryLoading, error: queryError, data: organizationData },
  ] = useLazyQuery<OrganizationData>(OrganizationQuery, {
    fetchPolicy: "cache-and-network",
    onError: () => {
      !message &&
        setMessage({
          title: t("company_info"),
          message: t("unable_load"),
        });
    },
    onCompleted: (data: any) => {
      if (!message && (queryError || !data)) {
        !message &&
          setMessage({
            title: t("company_info"),
            message: t("failed_load"),
          });
      }
    },
  });

  const [updateOrganization, { loading: updateOrganizationLoading }] =
    useMutation(UpdateOrganizationMutation, {
      onError: () => {
        !message &&
          setMessage({
            title: t("company_info"),
            message: t("unable_save"),
          });
      },
    });

  useEffect(() => {
    authCheckCallApi(getCompany);
  }, [getCompany]);

  type FormData = {
    companyName: string;
    addressLine1: string;
    addressLine2: string;
    addressCity: string;
    addressCountry: string;
    addressZip: string;
    addressState: string;
    addressTelephone1: string;
    addressTelephone2: string;
    [key: string]: any;
  };
  const emptyInputs = {
    companyName: "",
    addressLine1: "",
    addressLine2: "",
    addressCity: "",
    addressCountry: "",
    addressZip: "",
    addressState: "",
    addressTelephone1: "",
    addressTelephone2: "",
  };

  const [inputs, setInputs] = useState<FormData>(emptyInputs);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleDropdownChange = (event: any, data: DropdownProps) => {
    setValue(data.name, data.value);
    setInputs((inputs) => ({ ...inputs, [data.name]: data.value }));
  };

  const onSaveOrganization = async (data: any) => {
    const organizationInput: OrganizationInput = {
      name: data.companyName,
      address: {
        line1: data.addressLine1,
        line2: data.addressLine2,
        city: data.addressCity,
        country: data.addressCountry,
        zip: data.addressZip,
        state: data.addressState,
        telephone1: data.addressTelephone1,
        telephone2: data.addressTelephone2,
      },
    };

    authCheckCallApi(updateOrganization, {
      variables: { organization: { ...organizationInput } },
    });
  };

  if (
    !queryLoading &&
    !updateOrganizationLoading &&
    organizationData &&
    organizationData.organization &&
    currentOrganizationUpdated.current !== organizationData.organization.updated
  ) {
    const organization = organizationData.organization;
    currentOrganizationUpdated.current = organization.updated;
    inputs.companyName = organization.name;

    if (organization.address) {
      inputs.addressLine1 = organization.address.line1;
      inputs.addressLine2 = organization.address.line2 || "";
      inputs.addressCity = organization.address.city;
      inputs.addressCountry = organization.address.country.toLocaleLowerCase();
      inputs.addressZip = organization.address.zip;
      inputs.addressState = organization.address.state || "";
      inputs.addressTelephone1 = organization.address.telephone1 || "";
      inputs.addressTelephone2 = organization.address.telephone2 || "";
      setValue(
        "addressCountry",
        organization.address.country.toLocaleLowerCase()
      );
    }
  } else {
    setValue("addressCountry", inputs.addressCountry);
  }

  const { plan } = authData.signInUserSession.idToken.payload;
  const planInfo = getPlanInfo(plan);
  const readOnly: boolean = plan === "expired";
  const dimmerActive = queryLoading || updateOrganizationLoading || !!message;

  return (
    <React.Fragment>
      <Container id={styles.highlightsContainer}>
        <Transition transitionOnMount animation="fade in" duration="250">
          <Segment className={styles.myAccountSegment}>
            <Dimmer active={dimmerActive}>
              <Loader size="large" disabled={!!message} active={dimmerActive}>
                {(queryLoading && "Loading...") ||
                  (updateOrganizationLoading && "Saving...")}
              </Loader>
            </Dimmer>
            <Form onSubmit={handleSubmit(onSaveOrganization)}>
              <Grid>
                <Grid.Row style={{ paddingBottom: 0 }}>
                  <Grid.Column computer={12} mobile={16} tablet={16}>
                    <div className={styles.sectionHeaderMargin}>
                      {t("company_details")}
                    </div>

                    <Form.Field inline required error={!!errors["companyName"]}>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("company_name")}
                        </label>
                        <input
                          className={styles.myAccountInput}
                          placeholder={t("company")}
                          name="companyName"
                          key="companyName"
                          ref={register({
                            required: t("company_required") as string,
                          })}
                          defaultValue={inputs.companyName}
                          onChange={handleInputChange}
                          disabled={readOnly || !isAdmin}
                        />
                      </Form.Group>
                    </Form.Field>

                    <Form.Group inline>
                      <label className={styles.myAccountLabel}>
                        {t("sub_plan")}
                      </label>
                      <div
                        className={styles.myAccountInfoLabel}
                        style={{ color: planInfo.color }}
                      >
                        {planInfo.name}
                      </div>
                    </Form.Group>

                    <Form.Field inline required error={!!errors.addressLine1}>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("addr_line_1")}
                        </label>
                        <input
                          className={styles.myAccountInput}
                          placeholder={t("street_address")}
                          name="addressLine1"
                          key="addressLine1"
                          ref={register({
                            required: t("street_address_required") as string,
                          })}
                          defaultValue={inputs.addressLine1}
                          onChange={handleInputChange}
                          disabled={readOnly || !isAdmin}
                        />
                      </Form.Group>
                    </Form.Field>

                    <Form.Field inline>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("addr_line_2")}
                        </label>
                        <input
                          className={styles.myAccountInput}
                          name="addressLine2"
                          key="addressLine2"
                          ref={register({ required: false })}
                          placeholder={t("addr_line_2")}
                          defaultValue={inputs.addressLine2}
                          onChange={handleInputChange}
                          disabled={readOnly || !isAdmin}
                        />
                      </Form.Group>
                    </Form.Field>

                    <Form.Field inline required error={!!errors.addressCity}>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("city_town")}
                        </label>
                        <input
                          className={styles.myAccountInput}
                          placeholder={t("city_town")}
                          name="addressCity"
                          key="addressCity"
                          ref={register({
                            required: t("city_required") as string,
                          })}
                          defaultValue={inputs.addressCity}
                          onChange={handleInputChange}
                          disabled={readOnly || !isAdmin}
                        />
                      </Form.Group>
                    </Form.Field>

                    <Form.Field inline required error={!!errors.addressZip}>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("zip_code")}
                        </label>
                        <input
                          className={styles.myAccountInput}
                          placeholder={t("zip_post")}
                          name="addressZip"
                          key="addressZip"
                          ref={register({
                            required: t("zip_required") as string,
                          })}
                          defaultValue={inputs.addressZip}
                          onChange={handleInputChange}
                          disabled={readOnly || !isAdmin}
                        />
                      </Form.Group>
                    </Form.Field>

                    <Form.Field inline>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("state")}
                        </label>
                        <input
                          className={styles.myAccountInput}
                          placeholder={t("state_country")}
                          name="addressState"
                          key="addressState"
                          ref={register({ required: false })}
                          defaultValue={inputs.addressState}
                          onChange={handleInputChange}
                          disabled={readOnly || !isAdmin}
                        />
                      </Form.Group>
                    </Form.Field>

                    <Form.Field inline required error={!!errors.addressCountry}>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("country")}
                        </label>
                        <Dropdown
                          className={styles.myAccountInput}
                          name="addressCountry"
                          key="addressCountry"
                          fluid
                          search
                          selection
                          options={countryOptions}
                          placeholder="Select Country"
                          value={inputs.addressCountry}
                          onChange={handleDropdownChange}
                          disabled={readOnly || !isAdmin}
                          icon={
                            <Ico name="down" className={styles.dropdownIcon} />
                          }
                        />
                      </Form.Group>
                    </Form.Field>

                    <Form.Field inline>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("telephone_1")}
                        </label>
                        <input
                          className={styles.myAccountInput}
                          placeholder={t("telephone_1")}
                          name="addressTelephone1"
                          key="addressTelephone1"
                          ref={register({ required: false })}
                          defaultValue={inputs.addressTelephone1}
                          onChange={handleInputChange}
                          disabled={readOnly || !isAdmin}
                        />
                      </Form.Group>
                    </Form.Field>

                    <Form.Field inline>
                      <Form.Group inline>
                        <label className={styles.myAccountLabel}>
                          {t("telephone_2")}
                        </label>
                        <input
                          className={styles.myAccountInput}
                          placeholder={t("telephone_2")}
                          name="addressTelephone2"
                          key="addressTelephone2"
                          ref={register({ required: false })}
                          defaultValue={inputs.addressTelephone2}
                          onChange={handleInputChange}
                          disabled={readOnly || !isAdmin}
                        />
                      </Form.Group>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row style={{ paddingBottom: 0 }}>
                  {isAdmin && !readOnly && (
                    <Grid.Column stretched>
                      <div className={styles.buttonContainer}>
                        <Button primary className={cStyles.buttonPrime}>
                          <Ico name="check" style={{ marginRight: "10px" }} />
                          {t("save")}
                        </Button>
                      </div>
                    </Grid.Column>
                  )}
                </Grid.Row>
              </Grid>
            </Form>
          </Segment>
        </Transition>
      </Container>
      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </React.Fragment>
  );
};

export default Company;
